import { React, useState, useEffect, useRef } from "react";
import styled from "styled-components";

const Wrapper = styled.div`
    position: relative;
    width: 100%;
`

const Input = styled.input`
    width: 100%;
    padding: 9px 10px;
    border-radius: 8px;
    border: 1px solid #e6e6e6;
    background: #ffffff;
    font-family: 'Helvetica Neue', Helvetica, Arial, Sans-Serif;
    font-size: 14px;
    color: #0d0c1d;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #fc3a34;
    }
`

const List = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 4px 0 0;
    padding: 4px 0;
    list-style: none;
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    max-height: 240px;
    overflow-y: auto;
    z-index: 20;
`

const Item = styled.li`
    padding: 8px 12px;
    font-size: 14px;
    color: #0d0c1d;
    cursor: pointer;

    &.highlighted {
        background: #f5f5f5;
    }

    &:hover {
        background: #f5f5f5;
    }
`

const NoResults = styled.li`
    padding: 8px 12px;
    font-size: 13px;
    color: #8a8a8a;
`

const StationAutocomplete = ({ stations, value, onChange, placeholder }) => {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const wrapperRef = useRef(null);

    const matches = (query.trim()
        ? stations.filter((s) => s.label.toLowerCase().includes(query.trim().toLowerCase()))
        : stations
    ).slice(0, 100);

    // Sync the input text whenever the selected value changes externally (reset).
    useEffect(() => {
        if (!value) {
            setQuery("");
            return;
        }
        const station = stations.find((s) => s.key === value);
        setQuery(station ? station.label : "");
    }, [value, stations]);

    // Close the list when clicking outside.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const select = (station) => {
        onChange(station.key);
        setQuery(station.label);
        setOpen(false);
        setHighlighted(-1);
    };

    const handleKeyDown = (event) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setHighlighted((h) => (h + 1) % matches.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlighted((h) => (h <= 0 ? matches.length - 1 : h - 1));
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (open && highlighted >= 0 && matches[highlighted]) {
                select(matches[highlighted]);
            }
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <Wrapper ref={wrapperRef}>
            <Input
                type="text"
                value={query}
                placeholder={placeholder}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setOpen(true);
                    setHighlighted(-1);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
            />
            {open && (
                <List role="listbox">
                    {matches.length > 0 ? (
                        matches.map((s, i) => (
                            <Item
                                key={s.key}
                                className={i === highlighted ? "highlighted" : ""}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => select(s)}
                                role="option"
                                aria-selected={s.key === value}
                            >
                                {s.label}
                            </Item>
                        ))
                    ) : (
                        <NoResults>No stations found</NoResults>
                    )}
                </List>
            )}
        </Wrapper>
    );
};

export default StationAutocomplete;
